import React, { useState, useEffect, useRef } from 'react';
import ImageCropper from '../../components/admin/ImageCropper';

const emptyItem = {
  id: '',
  title: '',
  companyType: '',
  employeeCount: '',
  content: '',
  image: null,
};

const TestimonialAdmin = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyItem);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [showFilePicker, setShowFilePicker] = useState(false);
  const cropperRef = useRef(null);

  useEffect(() => {
    fetch('/data/testimonials.json?t=' + Date.now())
      .then((r) => r.ok ? r.json() : [])
      .then(setItems)
      .catch(() => setItems([]));
  }, []);

  const fetchFiles = () => {
    fetch('/api/admin-files.php?action=list')
      .then((r) => r.ok ? r.json() : { files: [] })
      .then((d) => setFiles(d.files || []))
      .catch(() => setFiles([]));
  };

  const startNew = () => {
    setForm({ ...emptyItem, id: Date.now() });
    setEditing('new');
    setMessage('');
    setShowFilePicker(false);
    fetchFiles();
  };

  const startEdit = (index) => {
    setForm({ ...items[index] });
    setEditing(index);
    setMessage('');
    setShowFilePicker(false);
    fetchFiles();
  };

  const cancel = () => { setEditing(null); setForm(emptyItem); setShowFilePicker(false); };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const save = async () => {
    setSaving(true);
    setMessage('');
    let updated;
    if (editing === 'new') {
      updated = [...items, form];
    } else {
      updated = items.map((item, i) => (i === editing ? form : item));
    }
    try {
      const res = await fetch('/api/admin-data.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: 'testimonials', data: updated }),
      });
      const data = await res.json();
      if (data.success) {
        setItems(updated);
        setEditing(null);
        setForm(emptyItem);
        setMessage('保存しました');
      } else {
        setMessage('保存に失敗しました');
      }
    } catch {
      setMessage('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (index) => {
    if (!window.confirm('削除しますか？')) return;
    const updated = items.filter((_, i) => i !== index);
    try {
      await fetch('/api/admin-data.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: 'testimonials', data: updated }),
      });
      setItems(updated);
      setMessage('削除しました');
    } catch {
      setMessage('削除に失敗しました');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">お客様の声 管理</h2>
        {editing === null && (
          <button onClick={startNew} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">
            + 新規追加
          </button>
        )}
      </div>

      {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

      {editing !== null && (
        <div className="bg-white rounded-xl shadow p-6 mb-6">
          <h3 className="font-bold text-lg mb-4">{editing === 'new' ? '新規追加' : '編集'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="タイトル" value={form.title} onChange={(v) => updateField('title', v)} />
            <Field label="業種" value={form.companyType} onChange={(v) => updateField('companyType', v)} />
            <Field label="従業員数" value={form.employeeCount} onChange={(v) => updateField('employeeCount', v)} />
          </div>
          <div className="mt-4">
            <label className="block text-sm font-bold mb-1">写真</label>
            {form.image && form.image.url ? (
              <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3 mb-2">
                <img src={form.image.url} alt="" className="w-16 h-16 object-cover rounded border" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{form.image.url.split('/').pop()}</p>
                </div>
                <button type="button" onClick={() => updateField('image', null)} className="text-red-500 text-xs font-bold hover:text-red-700">削除</button>
              </div>
            ) : null}
            <div className="flex gap-2">
              <ImageCropper
                ref={cropperRef}
                aspectRatio={NaN}
                maxWidth={800}
                maxHeight={600}
                onCropped={(img) => updateField('image', img)}
              />
              <button type="button" onClick={() => setShowFilePicker(!showFilePicker)}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">
                {showFilePicker ? '閉じる' : '既存ファイルから選択'}
              </button>
            </div>
            {showFilePicker && (
              <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                {files.filter((f) => /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(f.name)).length === 0 ? (
                  <p className="px-3 py-4 text-sm text-gray-400 text-center">画像ファイルがありません</p>
                ) : (
                  files.filter((f) => /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(f.name)).map((file, idx) => {
                    const selected = form.image && form.image.url === file.url;
                    return (
                      <button key={idx} type="button"
                        onClick={() => { cropperRef.current?.openWithUrl(file.url); setShowFilePicker(false); }}
                        className={`w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-3 border-b last:border-b-0 ${selected ? 'bg-blue-50' : ''}`}>
                        <img src={file.url} alt={file.name} className="w-10 h-10 object-cover rounded border shrink-0" />
                        <span className={`text-sm truncate ${selected ? 'font-bold text-blue-700' : 'text-gray-700'}`}>{file.name}</span>
                        {selected && <span className="text-blue-600 text-xs font-bold shrink-0">選択中</span>}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
          <div className="mt-4">
            <label className="block text-sm font-bold mb-1">内容（HTML可）</label>
            <textarea
              value={form.content}
              onChange={(e) => updateField('content', e.target.value)}
              rows={6}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none font-mono"
            />
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={save} disabled={saving} className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
              {saving ? '保存中...' : '保存'}
            </button>
            <button onClick={cancel} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">
              キャンセル
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.length === 0 && editing === null && (
          <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">データがありません。</div>
        )}
        {items.map((item, i) => (
          <div key={item.id || i} className="bg-white rounded-xl shadow p-5 flex items-center justify-between">
            <div>
              <p className="font-bold">{item.title}</p>
              <p className="text-sm text-gray-500">{item.companyType} {item.employeeCount && `/ ${item.employeeCount}`}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => startEdit(i)} className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-bold hover:bg-gray-200">編集</button>
              <button onClick={() => remove(i)} className="bg-red-50 text-red-600 px-3 py-1.5 rounded-lg text-sm font-bold hover:bg-red-100">削除</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange }) => (
  <div>
    <label className="block text-sm font-bold mb-1">{label}</label>
    <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
  </div>
);

export default TestimonialAdmin;
